import { motion } from "framer-motion";
import { Check, Flame, Crown, Star } from "lucide-react";
import { trackInitiateCheckout, prepareStripeCheckout } from "@/lib/metaPixel";
import { STRIPE_VIP_URL, STRIPE_STANDARD_URL, STANDARD_SOLD_OUT, EVENT_SOLD_OUT } from "@/lib/tickets";
import useCapacity from "@/lib/useCapacity";

const STANDARD = [
  "Място в залата — не запазено",
  "2 кафе паузи",
  "Достъп до всички 18 сесии с топ експерти и инвеститори (без ВИП зоната)",
  "Материали и презентации от събитието",
];

const VIP = [
  "Запазено място на първите редове във ВИП Зоната",
  "ВИП Коктейл с лекторите",
  "Достъп до ВИП обществото във Viber с Иво Димовски",
  "Запис на цялото събитие",
  "Достъп до всички 18 сесии с топ експерти и инвеститори",
];

const CTA_STANDARD = STRIPE_STANDARD_URL;
const CTA_VIP = STRIPE_VIP_URL;

function StandardCard({ remaining, soldOut }) {
  const gone = STANDARD_SOLD_OUT || soldOut;
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
      data-testid="ticket-standard"
      className="ticket-card relative overflow-hidden p-8 sm:p-10 lg:p-12 bg-[#0F2647] border border-[#F5EBD9]/10"
    >
      <div className={gone ? "opacity-40 pointer-events-none select-none" : ""}>
      <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] font-mono px-3 py-1.5 mb-8 bg-[#F5892E] text-[#0B1F3D]">
        <Flame className="w-3.5 h-3.5" strokeWidth={2.5} />
        Стандартен
      </div>

      <div className="font-mono text-[11px] uppercase tracking-[0.32em] mb-3 text-[#F5EBD9]/60">
        Ограничени места
      </div>
      <h3 className="font-display uppercase text-3xl sm:text-4xl mb-8 leading-[1.05] text-[#F5EBD9]">
        Стандартен вход.
      </h3>

      <div className="flex items-baseline gap-2 mb-2">
        <span className="font-display display-tight text-7xl sm:text-8xl text-[#F5EBD9]">69</span>
        <span className="font-mono text-lg text-[#F5EBD9]/70">€</span>
      </div>
      <div className="font-mono text-[11px] uppercase tracking-[0.32em] mb-10 text-[#F5EBD9]/60">
        включени всички такси
      </div>

      <ul className="space-y-4 mb-10">
        {STANDARD.map((f, i) => (
          <li key={i} className="flex items-start gap-3">
            <Check className="w-5 h-5 shrink-0 mt-0.5 text-[#F5892E]" strokeWidth={2.5} />
            <span className="text-[#F5EBD9]/85">{f}</span>
          </li>
        ))}
      </ul>

      <a
        href={CTA_STANDARD}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          if (gone) { e.preventDefault(); return; }
          e.preventDefault();
          const checkoutUrl = prepareStripeCheckout({
            url: CTA_STANDARD,
            ticketType: "standard",
            value: 69,
            currency: "EUR",
            contentName: "Стандартен билет — Играта на Имотите Варна",
          });
          trackInitiateCheckout({
            value: 69,
            currency: "EUR",
            content_name: "Стандартен билет — Играта на Имотите Варна",
            content_ids: ["standard-69"],
          });
          window.open(checkoutUrl, "_blank", "noopener,noreferrer");
        }}
        data-testid="ticket-standard-cta"
        className={`btn-accent block w-full text-center px-8 py-4 rounded-full uppercase tracking-widest text-sm font-medium ${gone ? "opacity-40 pointer-events-none" : ""}`}
      >
        {gone ? "Разпродадено" : "Купи билет"}
      </a>
      <div className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.32em] text-[#F5EBD9]/60">
        {gone ? "Няма налични места" : `Остават ${remaining} места · Гарантирай своето`}
      </div>
      </div>

      {/* Giant SOLD OUT stamp */}
      {gone && (
        <div
          data-testid="standard-soldout-stamp"
          className="absolute inset-0 z-10 flex items-center justify-center bg-[#0B1F3D]/55 backdrop-blur-[2px]"
        >
          <div className="-rotate-12 border-4 sm:border-[6px] border-[#E5484D] text-[#E5484D] font-display uppercase text-5xl sm:text-7xl px-6 sm:px-10 py-3 sm:py-5 tracking-tight shadow-[0_0_60px_-10px_rgba(229,72,77,0.7)]">
            Sold Out
          </div>
        </div>
      )}
    </motion.div>
  );
}

function VipCard({ vipRemaining, soldOut }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.9, delay: 0.05, ease: [0.2, 0.8, 0.2, 1] }}
      data-testid="ticket-vip"
      className="ticket-card relative overflow-hidden p-8 sm:p-10 lg:p-12 bg-gradient-to-br from-[#1c1b1a] via-[#0B1F3D] to-[#12294F] border border-[#E0B95C]/50 shadow-[0_40px_100px_-20px_rgba(224,185,92,0.35)] lg:scale-[1.03]"
    >
      {/* Glowing sheen */}
      <div className="pointer-events-none absolute -top-1/2 -right-1/4 w-[80%] h-[140%] bg-[radial-gradient(ellipse_at_center,rgba(224,185,92,0.22),transparent_65%)]" />
      <div className="pointer-events-none absolute -bottom-1/3 -left-1/4 w-[70%] h-[100%] bg-[radial-gradient(ellipse_at_center,rgba(245,137,46,0.14),transparent_65%)]" />

      {/* Recommended ribbon */}
      <div
        data-testid="vip-recommended-ribbon"
        className="absolute top-6 right-[-42px] rotate-45 bg-gradient-to-r from-[#E0B95C] via-[#F5CE72] to-[#E0B95C] text-[#0B1F3D] px-14 py-1.5 font-mono text-[10px] uppercase tracking-[0.32em] shadow-lg z-10"
      >
        Препоръчано
      </div>

      {/* Sparkles */}
      <Star className="absolute top-8 left-1/2 w-3 h-3 text-[#E0B95C]/70 animate-pulse" fill="currentColor" />
      <Star className="absolute top-24 left-1/3 w-2 h-2 text-[#F5892E]/50 animate-pulse" style={{ animationDelay: "600ms" }} fill="currentColor" />
      <Star className="absolute bottom-32 right-1/4 w-2.5 h-2.5 text-[#E0B95C]/60 animate-pulse" style={{ animationDelay: "1200ms" }} fill="currentColor" />

      <div className={`relative ${soldOut ? "opacity-40 pointer-events-none select-none" : ""}`}>
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] font-mono px-3 py-1.5 mb-8 bg-gradient-to-r from-[#E0B95C] to-[#F5CE72] text-[#0B1F3D]">
          <Crown className="w-3.5 h-3.5" strokeWidth={2.5} />
          VIP Достъп
        </div>

        <div className="font-mono text-[11px] uppercase tracking-[0.32em] mb-3 text-[#E0B95C]">
          Първи редове · Затворена мрежа
        </div>
        <h3 className="font-display uppercase text-3xl sm:text-4xl mb-2 leading-[1.05] text-[#F5EBD9]">
          ВИП преживяване.
        </h3>
        <div className="font-serif text-[#E0B95C]/90 text-lg mb-8">
          За тези, които идват за връзките.
        </div>

        <div className="flex items-baseline gap-2 mb-2">
          <span
            className="font-display display-tight text-7xl sm:text-8xl bg-gradient-to-b from-[#F5EBD9] via-[#F5CE72] to-[#E0B95C] bg-clip-text text-transparent"
            style={{ WebkitBackgroundClip: "text" }}
          >
            119
          </span>
          <span className="font-mono text-lg text-[#F5EBD9]/70">€</span>
        </div>
        <div className="font-mono text-[11px] uppercase tracking-[0.32em] mb-10 text-[#F5EBD9]/60">
          включени всички такси
        </div>

        <ul className="space-y-4 mb-10">
          {VIP.map((f, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className="w-5 h-5 shrink-0 mt-0.5 rounded-full bg-gradient-to-br from-[#E0B95C] to-[#F5892E] flex items-center justify-center">
                <Check className="w-3 h-3 text-[#0B1F3D]" strokeWidth={3} />
              </div>
              <span className="text-[#F5EBD9]/90">{f}</span>
            </li>
          ))}
        </ul>

        <a
          href={CTA_VIP}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            if (soldOut) { e.preventDefault(); return; }
            e.preventDefault();
            const checkoutUrl = prepareStripeCheckout({
              url: CTA_VIP,
              ticketType: "vip",
              value: 119,
              currency: "EUR",
              contentName: "VIP Достъп — Играта на Имотите Варна",
            });
            trackInitiateCheckout({
              value: 119,
              currency: "EUR",
              content_name: "VIP Достъп — Играта на Имотите Варна",
              content_ids: ["vip-119"],
            });
            window.open(checkoutUrl, "_blank", "noopener,noreferrer");
          }}
          data-testid="ticket-vip-cta"
          className={`block w-full text-center px-8 py-4 rounded-full uppercase tracking-widest text-sm font-medium bg-gradient-to-r from-[#E0B95C] via-[#F5CE72] to-[#E0B95C] text-[#0B1F3D] hover:from-[#F5EBD9] hover:to-[#F5EBD9] transition-all duration-500 shadow-[0_10px_30px_-5px_rgba(224,185,92,0.6)] ${soldOut ? "opacity-40 pointer-events-none" : ""}`}
        >
          {soldOut ? "Разпродадено" : "Купи VIP Достъп"}
        </a>

        <div className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.32em] text-[#E0B95C]" data-testid="vip-remaining-note">
          {soldOut ? "Няма налични VIP места" : `Остават само ${vipRemaining} VIP билета · Последни места на първи редове`}
        </div>
      </div>

      {/* Giant SOLD OUT stamp */}
      {soldOut && (
        <div
          data-testid="vip-soldout-stamp"
          className="absolute inset-0 z-10 flex items-center justify-center bg-[#0B1F3D]/55 backdrop-blur-[2px]"
        >
          <div className="-rotate-12 border-4 sm:border-[6px] border-[#E5484D] text-[#E5484D] font-display uppercase text-5xl sm:text-7xl px-6 sm:px-10 py-3 sm:py-5 tracking-tight shadow-[0_0_60px_-10px_rgba(229,72,77,0.7)]">
            Sold Out
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function Tickets() {
  const { remaining, tier, vip_remaining, vip_total } = useCapacity();
  const soldOut = EVENT_SOLD_OUT || tier === "sold_out";
  const vipSold = Math.max(0, (vip_total || 50) - (vip_remaining || 0));
  const vipPercent = vip_total > 0 ? Math.min(100, Math.round((vipSold / vip_total) * 100)) : 0;

  return (
    <section
      id="tickets"
      data-testid="tickets-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-[#0B1F3D] text-[#F5EBD9] overflow-hidden"
    >
      <div className="absolute inset-0 sunset-glow opacity-50 pointer-events-none" />
      <div className="max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          className="mb-10 lg:mb-14 max-w-4xl"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#F5892E]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#F5EBD9]/70">
              {EVENT_SOLD_OUT ? "Билети · Разпродадено" : `Финал — Билети · Остават само ${vip_remaining} VIP`}
            </span>
          </div>
          {EVENT_SOLD_OUT ? (
            <>
              <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                Всички билети са <span className="text-[#E5484D]">разпродадени</span>.
              </h2>
              <p className="mt-6 text-lg text-[#F5EBD9]/75 max-w-[52ch]">
                Не очаквахме този огромен интерес — благодарим на всички, които ще бъдат с нас на
                24.10 във Варна! А на 25.10 обявяваме нещо специално — запиши имейла си, за да не
                го пропуснеш.
              </p>
              <button
                onClick={() => document.getElementById("notify")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="tickets-notify-cta"
                className="mt-8 inline-flex items-center gap-3 px-8 py-4 rounded-full uppercase tracking-widest text-sm font-bold bg-gradient-to-r from-[#E0B95C] via-[#F5CE72] to-[#E0B95C] text-[#0B1F3D] hover:scale-[1.03] transition-transform shadow-[0_15px_40px_-10px_rgba(224,185,92,0.7)]"
              >
                Запиши имейла си
              </button>
            </>
          ) : (
            <>
              <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                Стандартните билети са <span className="text-[#E5484D]">изчерпани</span>. Остават само{" "}
                <span className="text-[#E0B95C]">{vip_remaining} VIP места</span>.
              </h2>
              <p className="mt-6 text-lg text-[#F5EBD9]/75 max-w-[52ch]">
                Стандартните билети са разпродадени. Останаха последните VIP места на първите
                редове — с ВИП коктейл, затворена мрежа и запис на цялото събитие.
              </p>
            </>
          )}
        </motion.div>

        {/* Live capacity indicator — hidden when the event is sold out */}
        {!EVENT_SOLD_OUT && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8 }}
          data-testid="tickets-capacity-bar"
          className="mb-12 sm:mb-16 bg-[#0F2647] border border-[#F5EBD9]/10 p-5 sm:p-6 rounded-2xl max-w-3xl"
        >
          <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#E0B95C] mb-1">
                Живо преброяване · VIP
              </div>
              <div className="font-display uppercase text-xl sm:text-2xl">
                {vip_remaining <= 0 ? "РАЗПРОДАДЕНО" : `Остават само ${vip_remaining} VIP билета`}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#F5EBD9]/60">
                VIP заети
              </div>
              <div className="font-display text-2xl sm:text-3xl text-[#F5EBD9]">
                {vipSold} <span className="text-[#F5EBD9]/50">/ {vip_total}</span>
              </div>
            </div>
          </div>
          <div className="h-2 bg-[#0B1F3D] border border-[#F5EBD9]/10 overflow-hidden rounded-full">
            <motion.div
              initial={{ width: "0%" }}
              whileInView={{ width: `${vipPercent}%` }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, ease: [0.2, 0.8, 0.2, 1] }}
              className="h-full bg-gradient-to-r from-[#E0B95C] via-[#F5CE72] to-[#E0B95C]"
            />
          </div>
        </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-start">
          <StandardCard remaining={remaining} soldOut={soldOut} />
          <VipCard vipRemaining={vip_remaining} soldOut={soldOut || vip_remaining <= 0} />
        </div>

        <p className="mt-12 text-center font-mono text-[11px] uppercase tracking-[0.32em] text-[#F5EBD9]/60">
          Хотел Черно Море · Варна · 24 октомври 2026
        </p>
      </div>
    </section>
  );
}
